<template>
  <div class="dashboard-body">
    <div class="row">
      <sidenav class="custom-col-width-2" />
      <div class="custom-col-width-10">
        <navbar />
        <div class="content-section">
          <section class="list-page">
            <header class="list-page__head">
              <div class="list-page__heading">
                <h2 class="list-page__title">Verifications</h2>
                <p class="list-page__count">{{ countLabel }}</p>
              </div>
              <button type="button" class="list-page__action" :disabled="isLoading" v-on:click="loadVerifications">
                <i :class="['pi', isLoading ? 'pi-spin pi-spinner' : 'pi-refresh']"></i>
                <span>Refresh</span>
              </button>
            </header>

            <div class="verifications-toolbar">
              <div class="search-box">
                <i class="pi pi-search"></i>
                <input
                  v-model.trim="searchTerm"
                  type="search"
                  class="form-control shadow-none"
                  placeholder="Search by account, reference, transaction id…"
                />
              </div>
              <select
                v-model="statusFilter"
                class="form-control shadow-none status-filter"
                v-on:change="onStatusFilterChange"
              >
                <option value="">All statuses</option>
                <option v-for="status in statusOptions" :key="status" :value="status">{{ statusLabel(status) }}</option>
              </select>
            </div>

            <div class="list-page__table">
              <tableLoader v-if="isLoading" message="Loading verifications…" />
              <div v-if="!isLoading" class="table-scroller">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Bank</th>
                      <th scope="col">Amount</th>
                      <th scope="col">Reference</th>
                      <th scope="col">Account</th>
                      <th scope="col">Status</th>
                      <th scope="col">Date</th>
                      <th scope="col" class="col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(v, index) in filteredVerifications" v-bind:key="v.id || index">
                      <th scope="row">{{ rowNumber(index) }}</th>
                      <td>{{ bankLabel(v.bank) }}</td>
                      <td class="row-name">{{ money(v.amount, v.currency) }}</td>
                      <td><span class="ref-text">{{ v.transactionReference || v.transactionId || '—' }}</span></td>
                      <td>
                        <router-link
                          v-if="accountDetailRoute(v)"
                          :to="accountDetailRoute(v)"
                          class="account-link"
                        >
                          {{ accountName(v) }}
                        </router-link>
                        <span v-else class="account-link account-link--disabled">{{ accountName(v) }}</span>
                      </td>
                      <td><span class="badge" :class="statusBadgeClass(v.status)">{{ statusLabel(v.status) }}</span></td>
                      <td>{{ $filters.getDateOnly(v.date || v.createdAt) }}</td>
                      <td class="col-actions">
                        <button type="button" class="view-btn" v-on:click="viewVerification(v)">View</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="!isLoading && filteredVerifications.length === 0" class="list-page__empty">
                <i class="pi pi-inbox"></i>
                <div>{{ searchTerm ? 'No verifications match your search.' : 'No verifications found.' }}</div>
              </div>
            </div>

            <pagination
              v-if="!isLoading && metaData"
              :meta-data="metaData"
              :current-page="currentPage"
              :per-page="perPage"
              v-on:change="onPageChange"
            />
          </section>
        </div>
      </div>
    </div>

    <verificationDetailModal
      v-if="showDetailModal && selectedVerification"
      :verification="selectedVerification"
      @close-detail-modal="showDetailModal = false"
      @refresh-verifications="loadVerifications"
    />
  </div>
</template>
<script>
import sidenav from "@/components/Sidenav.vue";
import navbar from "@/components/Navbar.vue";
import tableLoader from "@/components/Table_Loader.vue";
import pagination from "@/components/Pagination.vue";
import verificationDetailModal from "@/components/verification/Verification_Detail_Modal.vue";
import Verification from "@/services/verification";
import { BANK_LABELS, VERIFICATION_STATUS } from "@/utils/enums";
import { DEFAULT_CURRENCY } from "@/utils/const";
import { ACCOUNT_DETAIL_ROUTE } from "@/router/routes";

const STATUS_BADGE = {
  [VERIFICATION_STATUS.verified]: "badge-success",
  [VERIFICATION_STATUS.pending]: "badge-warning",
  [VERIFICATION_STATUS.rejected]: "badge-danger",
  [VERIFICATION_STATUS.failed]: "badge-danger",
};

export default {
  title: "Verifications",
  components: {
    sidenav,
    navbar,
    tableLoader,
    pagination,
    verificationDetailModal,
  },
  data() {
    return {
      verifications: [],
      metaData: null,
      isLoading: false,
      currentPage: 1,
      perPage: 30,
      searchTerm: "",
      statusFilter: "",
      selectedVerification: null,
      showDetailModal: false,
      statusOptions: [
        VERIFICATION_STATUS.verified,
        VERIFICATION_STATUS.pending,
        VERIFICATION_STATUS.rejected,
        VERIFICATION_STATUS.failed,
      ],
    };
  },
  computed: {
    // Client-side refinement of the loaded page (the backend has no free-text
    // search), matching the verifications list pattern in the user app.
    filteredVerifications() {
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) return this.verifications;
      return this.verifications.filter((v) => {
        const haystack = [
          this.accountName(v),
          this.creatorName(v),
          v.transactionReference,
          v.transactionId,
          v.accountName,
          v.bank,
          v.amount,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      });
    },
    countLabel() {
      const total = this.metaData ? this.metaData.totalRecords : this.verifications.length;
      return total === 1 ? "1 verification" : `${total} verifications`;
    },
  },
  methods: {
    rowNumber(index) {
      return (this.currentPage - 1) * this.perPage + index + 1;
    },
    accountName(v) {
      if (this.creatorName(v)) return this.creatorName(v);
      if (v.organization && v.organization.name) return v.organization.name;
      return this.creatorName(v) || "—";
    },
    accountId(v) {
      return (v.user && v.user.id) || v.createdBy || "";
    },
    accountDetailRoute(v) {
      const id = this.accountId(v);
      return id ? { name: ACCOUNT_DETAIL_ROUTE, params: { id } } : null;
    },
    creatorName(v) {
      return v.user ? this.$filters.fullName(v.user.fname, v.user.lname) : "";
    },
    bankLabel(bank) {
      return BANK_LABELS[bank] || String(bank || "").toUpperCase();
    },
    statusLabel(status) {
      return status ? status.charAt(0).toUpperCase() + status.slice(1) : "—";
    },
    statusBadgeClass(status) {
      return STATUS_BADGE[status] || "badge-secondary";
    },
    money(value, currency) {
      const amount = Number(value);
      if (isNaN(amount)) return "—";
      return this.$filters.formatPrice(amount, currency || DEFAULT_CURRENCY);
    },
    async loadVerifications() {
      this.isLoading = true;
      try {
        const params = { page: this.currentPage, limit: this.perPage };
        if (this.statusFilter) params.status = this.statusFilter;
        const res = await Verification.getVerifications(params);
        if (res?.data?.status) {
          this.verifications = res.data.data || [];
          this.metaData = res.data.metaData || null;
        }
      } finally {
        this.isLoading = false;
      }
    },
    onPageChange({ page, perPage }) {
      this.currentPage = page;
      this.perPage = perPage;
      this.loadVerifications();
    },
    onStatusFilterChange() {
      this.currentPage = 1;
      this.loadVerifications();
    },
    viewVerification(v) {
      this.selectedVerification = v;
      this.showDetailModal = true;
    },
  },
  mounted() {
    this.loadVerifications();
  },
};
</script>
<style scoped>
.verifications-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex: 1 1 340px;
  max-width: 460px;
}

.search-box .pi {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  color: var(--ehs-color-soft);
  font-size: 0.85rem;
}

.search-box .form-control {
  padding-left: 36px !important;
}

.status-filter {
  width: auto;
  min-width: 160px;
}

.account-link {
  font-weight: 600;
  color: var(--ehs-color-ink);
  text-decoration: none;
  text-transform: capitalize;
}

.account-link:hover {
  color: var(--ehs-color-primary);
}

.account-link--disabled {
  color: var(--ehs-color-soft);
  cursor: default;
}

.ref-text {
  font-family: var(--ehs-font-mono);
  font-size: 0.78rem;
  color: var(--ehs-color-text);
}

.view-btn {
  border: 0;
  background: transparent;
  padding: 4px 4px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--ehs-color-primary-active);
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.15s ease;
}

.view-btn:hover {
  color: var(--ehs-color-primary-hover);
  text-decoration: underline;
}

.view-btn:focus-visible {
  outline: none;
  text-decoration: underline;
}
</style>
